import { Button } from "@/components/core/buttons/button";
import {
	leadStatusesQueryOptions,
	useLeadStatusCreateMutation,
	useLeadStatusDeleteMutation,
	useLeadStatusUpdateMutation,
} from "@/features/dashboard/api/queries/lead-query";
import type { LeadStatus } from "@/features/dashboard/types/lead";
import * as Dialog from "@radix-ui/react-dialog";
import { IconArrowDown, IconArrowUp, IconCheck, IconPencil, IconPlus, IconSettings, IconTrash, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "sonner";

const PRESET_COLORS = [
	{ key: "green", bg: "bg-green-500", ring: "ring-green-500" },
	{ key: "yellow", bg: "bg-yellow-500", ring: "ring-yellow-500" },
	{ key: "red", bg: "bg-red-500", ring: "ring-red-500" },
	{ key: "blue", bg: "bg-blue-500", ring: "ring-blue-500" },
	{ key: "purple", bg: "bg-purple-500", ring: "ring-purple-500" },
	{ key: "gray", bg: "bg-gray-500", ring: "ring-gray-500" },
	{ key: "orange", bg: "bg-orange-500", ring: "ring-orange-500" },
] as const;

function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
	return (
		<div className="flex items-center gap-1.5">
			{PRESET_COLORS.map((c) => (
				<button
					key={c.key}
					type="button"
					onClick={() => onChange(c.key)}
					className={clsx("w-5 h-5 rounded-full transition-all", c.bg, value === c.key && "ring-2 ring-offset-1", value === c.key && c.ring)}
				/>
			))}
		</div>
	);
}

function StatusRow({
	status,
	statuses,
	onMoveUp,
	onMoveDown,
}: {
	status: LeadStatus;
	statuses: LeadStatus[];
	onMoveUp: () => void;
	onMoveDown: () => void;
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [editName, setEditName] = useState(status.name);
	const [editColor, setEditColor] = useState(status.color);
	const updateMutation = useLeadStatusUpdateMutation();
	const deleteMutation = useLeadStatusDeleteMutation();

	const isFirst = statuses[0]?.id === status.id;
	const isLast = statuses[statuses.length - 1]?.id === status.id;

	const handleSave = () => {
		if (!editName.trim()) return;
		updateMutation.mutate(
			{ id: status.id, name: editName.trim(), color: editColor },
			{
				onSuccess: () => {
					setIsEditing(false);
					toast.success("Status updated");
				},
				onError: (err: unknown) => {
					const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to update";
					toast.error(msg);
				},
			},
		);
	};

	const handleDelete = () => {
		deleteMutation.mutate(status.id, {
			onSuccess: () => toast.success("Status deleted"),
			onError: (err: unknown) => {
				const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to delete";
				toast.error(msg);
			},
		});
	};

	const colorDot = PRESET_COLORS.find((c) => c.key === (isEditing ? editColor : status.color));

	if (isEditing) {
		return (
			<div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
				<div className={clsx("w-3 h-3 rounded-full shrink-0", colorDot?.bg || "bg-gray-400")} />
				<input
					type="text"
					value={editName}
					onChange={(e) => setEditName(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleSave()}
					className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					ref={(el) => el?.focus()}
				/>
				<ColorPicker value={editColor} onChange={setEditColor} />
				<button type="button" onClick={handleSave} disabled={updateMutation.isPending} className="p-1 text-green-600 hover:text-green-700">
					<IconCheck size={16} />
				</button>
				<button
					type="button"
					onClick={() => {
						setIsEditing(false);
						setEditName(status.name);
						setEditColor(status.color);
					}}
					className="p-1 text-gray-400 hover:text-gray-600"
				>
					<IconX size={16} />
				</button>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg group">
			<div className={clsx("w-3 h-3 rounded-full shrink-0", colorDot?.bg || "bg-gray-400")} />
			<span className="flex-1 text-sm font-medium text-gray-900">{status.name}</span>
			<div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
				<button type="button" onClick={onMoveUp} disabled={isFirst} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
					<IconArrowUp size={14} />
				</button>
				<button type="button" onClick={onMoveDown} disabled={isLast} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
					<IconArrowDown size={14} />
				</button>
				<button type="button" onClick={() => setIsEditing(true)} className="p-1 text-gray-400 hover:text-blue-600">
					<IconPencil size={14} />
				</button>
				<button type="button" onClick={handleDelete} disabled={deleteMutation.isPending} className="p-1 text-gray-400 hover:text-red-600">
					<IconTrash size={14} />
				</button>
			</div>
		</div>
	);
}

function AddStatusRow() {
	const [name, setName] = useState("");
	const [color, setColor] = useState("gray");
	const createMutation = useLeadStatusCreateMutation();

	const handleAdd = () => {
		if (!name.trim()) return;
		createMutation.mutate(
			{ name: name.trim(), color },
			{
				onSuccess: () => {
					setName("");
					setColor("gray");
					toast.success("Status created");
				},
				onError: (err: unknown) => {
					const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to create";
					toast.error(msg);
				},
			},
		);
	};

	return (
		<div className="flex items-center gap-2 p-2 border border-dashed border-gray-300 rounded-lg">
			<IconPlus size={14} className="text-gray-400 shrink-0" />
			<input
				type="text"
				value={name}
				onChange={(e) => setName(e.target.value)}
				onKeyDown={(e) => e.key === "Enter" && handleAdd()}
				placeholder="New status name..."
				className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
			/>
			<ColorPicker value={color} onChange={setColor} />
			<Button size="sm" onClick={handleAdd} disabled={!name.trim() || createMutation.isPending}>
				Add
			</Button>
		</div>
	);
}

export function LeadStatusManager() {
	const [open, setOpen] = useState(false);
	const statusesQuery = useQuery(leadStatusesQueryOptions());
	const statuses = statusesQuery.data || [];
	const updateMutation = useLeadStatusUpdateMutation();

	const handleReorder = (statusId: string, direction: "up" | "down") => {
		const idx = statuses.findIndex((s) => s.id === statusId);
		if (idx < 0) return;
		const swapIdx = direction === "up" ? idx - 1 : idx + 1;
		if (swapIdx < 0 || swapIdx >= statuses.length) return;

		const current = statuses[idx];
		const swap = statuses[swapIdx];

		updateMutation.mutate({ id: current.id, sort_order: swap.sort_order });
		updateMutation.mutate({ id: swap.id, sort_order: current.sort_order });
	};

	return (
		<Dialog.Root open={open} onOpenChange={setOpen}>
			<Dialog.Trigger asChild>
				<button type="button" className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Manage Statuses">
					<IconSettings size={16} />
				</button>
			</Dialog.Trigger>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border bg-white p-6 shadow-lg rounded-lg">
					<div className="flex items-center justify-between mb-4">
						<Dialog.Title className="text-lg font-semibold">Manage Lead Statuses</Dialog.Title>
						<Dialog.Close asChild>
							<button type="button" className="p-1 text-gray-400 hover:text-gray-600">
								<IconX size={18} />
							</button>
						</Dialog.Close>
					</div>

					<div className="flex flex-col gap-1 mb-4 max-h-[400px] overflow-y-auto">
						{statusesQuery.isLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
							</div>
						) : statuses.length === 0 ? (
							<p className="text-sm text-gray-500 text-center py-4">No statuses yet. Add one below.</p>
						) : (
							statuses.map((status) => (
								<StatusRow
									key={status.id}
									status={status}
									statuses={statuses}
									onMoveUp={() => handleReorder(status.id, "up")}
									onMoveDown={() => handleReorder(status.id, "down")}
								/>
							))
						)}
					</div>

					<AddStatusRow />
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
